import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { API } from '../App';

export default function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('loading'); // loading, success, failed
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!sessionId) {
        setStatus('failed');
        return;
      }

      let attempts = 0;
      const maxAttempts = 5;
      const pollInterval = 2000;

      const poll = async () => {
        try {
          const response = await axios.get(`${API}/payments/stripe/status/${sessionId}`);
          setPaymentData(response.data);
          
          if (response.data.payment_status === 'paid') {
            setStatus('success');
            return;
          } else if (response.data.status === 'expired') {
            setStatus('failed');
            return;
          }
          
          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(poll, pollInterval);
          } else {
            // Assume success after polling if no failure
            setStatus(response.data.payment_status === 'unpaid' ? 'failed' : 'success');
          }
        } catch (error) {
          console.error('Failed to check payment status:', error);
          setStatus('failed');
        }
      };

      poll();
    };

    checkPaymentStatus();
  }, [sessionId]);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      
      <main className="flex-1 flex items-center justify-center px-6 py-16">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            {status === 'loading' && (
              <>
                <Loader2 className="h-16 w-16 text-primary animate-spin mx-auto mb-4" />
                <h1 className="text-2xl font-heading font-medium mb-2">Processing Payment</h1>
                <p className="text-muted-foreground">Please wait while we confirm your payment...</p>
              </>
            )}

            {status === 'success' && (
              <>
                <CheckCircle className="h-16 w-16 text-secondary mx-auto mb-4" />
                <h1 className="text-2xl font-heading font-medium mb-2" data-testid="payment-success-title">
                  Payment Successful!
                </h1>
                <p className="text-muted-foreground mb-6">
                  Thank you for your order. You will receive a confirmation email shortly.
                </p>
                {paymentData && (
                  <div className="bg-muted rounded-lg p-4 mb-6 text-left">
                    <p className="text-sm text-muted-foreground">Amount Paid</p>
                    <p className="text-xl font-semibold text-primary">
                      ${paymentData.amount?.toFixed(2)} {paymentData.currency?.toUpperCase()}
                    </p>
                  </div>
                )}
                <div className="flex flex-col gap-3">
                  <Link to="/dashboard">
                    <Button className="w-full" data-testid="view-orders-button">View My Orders</Button>
                  </Link>
                  <Link to="/products">
                    <Button variant="outline" className="w-full">Continue Shopping</Button>
                  </Link>
                </div>
              </>
            )}

            {status === 'failed' && (
              <>
                <XCircle className="h-16 w-16 text-destructive mx-auto mb-4" />
                <h1 className="text-2xl font-heading font-medium mb-2" data-testid="payment-failed-title">
                  Payment Failed
                </h1>
                <p className="text-muted-foreground mb-6">
                  Something went wrong with your payment. Please try again.
                </p>
                <div className="flex flex-col gap-3">
                  <Link to="/cart">
                    <Button className="w-full" data-testid="try-again-button">Try Again</Button>
                  </Link>
                  <Link to="/products">
                    <Button variant="outline" className="w-full">Continue Shopping</Button>
                  </Link>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </main>

      <Footer />
    </div>
  );
}
